/* eslint-disable camelcase */
/* eslint-disable consistent-return */
/* eslint-disable complexity */
import React, {
    useEffect, useState, useRef, useCallback, useMemo
} from 'react';
import FrameworkUtils from 'utils/framework';
import { Flex, Box } from 'components/ui';
import { gradients, mediaQueries, zIndices } from 'style/config';
import { EVENT_TYPES, MESSAGE_TYPES, RESPONSE_TYPES } from 'ai/constants/superchat';
import { CATEGORIES } from 'ai/constants/categories';

// Components
import SuperChatHeader from 'ai/components/SuperChat/SuperChatHeader/SuperChatHeader';
import PreSelectedPrompts from 'ai/components/SuperChat/PreSelectedPrompts/PreSelectedPrompts';
import InputBar from 'ai/components/SuperChat/InputBar/InputBar';
import ErrorMessage from 'ai/components/SuperChat/ErrorMessage/ErrorMessage';
import Messages from 'ai/components/SuperChat/Messages/Messages';
import InitialMessages from 'ai/components/SuperChat/InitialMessages/InitialMessages';
import SuperChatFlyout from 'ai/components/SuperChat/SuperChatFlyout/SuperChatFlyout';
import ContextualDivider from 'ai/components/SuperChat/ContextualDivider/ContextualDivider';
import ContextualChips from 'ai/components/SuperChat/ContextualChips/ContextualChips';
import WelcomeMat from 'ai/components/SuperChat/WelcomeMat/WelcomeMat';

// Utils
import {
    getNewEntryPoint, contextHasChanged, isGenAINewUIEnabledWithRCPS, getEcid
} from 'ai/utils/aiBeautyChat';
import {
    setGENAISession,
    removeLastFollowUps,
    getWelcomeMatTimeLeft,
    setPendingSignInEvent,
    getPendingSignInEvent,
    clearPendingSignInEvent
} from 'ai/utils/sessionStorage';
import { createDisplayVisibility } from './displayVisibility';
import mapPreviousConversations from 'ai/utils/mapPreviousConversations';
import findPreviousSession from 'ai/utils/findPreviousSession';

// Analytics
import { getSuperChatPageContextFromEntryPoint } from 'ai/utils/trackSuperChatPromptClick';
import { trackSignInCTAClick } from 'ai/analytics/trackers/interactionTrackers';

// Hooks
import useElementWidth from 'ai/hooks/useElementWidth';
import useSuperChatContext from 'ai/hooks/useSuperChatContext';
import useSuperChatFollowUps from 'ai/hooks/useSuperChatFollowUps';
import useSuperChatFeedback from 'ai/hooks/useSuperChatFeedback';
import useSuperChatScroll from 'ai/hooks/useSuperChatScroll';
import useSuperChatContextualChips from 'ai/hooks/useSuperChatContextualChips';
import { useSuperChatAnalytics } from 'ai/analytics';
import useSuperChatSocket from 'ai/hooks/useSuperChatSocket';

const { wrapFunctionalComponent } = FrameworkUtils;

function SuperChat(props) {
    const {
        localization,
        entryPointData: InitialEntryPointData,
        prompts,
        createSession,
        closeSuperChat,
        config,
        language,
        country,
        clientId,
        isSignInModalOpen,
        anonymousId,
        fetchPreviousConversations,
        setRequestInFlight,
        shouldShowWelcomeMat,
        showSignInModal
    } = props;

    // UI State
    const isGenAINewUIEnabled = isGenAINewUIEnabledWithRCPS();
    const [previousMessages, setPreviousMessages] = useState([]);
    const [messages, setMessages] = useState([]);
    const [sessionId, setSessionId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
    const [lastUserMessage, setLastUserMessage] = useState('');
    const [hasSelectedPrompt, setHasSelectedPrompt] = useState(false);
    const [isChatInitialized, setIsChatInitialized] = useState(false);
    const [welcomeMatCompletedOnMount] = useState(() => getWelcomeMatTimeLeft() !== '0s');
    const [welcomeMatPromptClicked, setWelcomeMatPromptClicked] = useState(false);

    // KillSwitches
    const { genAIChatBotWelcomeMat } = Sephora.configurationSettings;

    // Refs
    const aiBeautyChatRef = useRef(null);
    const lastProcessedEntryPointRef = useRef(null);
    const previousClientIdRef = useRef(clientId);
    const clearFollowUpsRef = useRef(null);
    const handleContextChangeRef = useRef(null);
    const sendMessageRef = useRef(null);
    const socketServiceRef = useRef(null);
    const handleCloseRef = useRef(null);
    const initializeRef = useRef(null);

    // Custom Hooks
    const contextualChipWidth = useElementWidth(aiBeautyChatRef);

    const { showFlyer, showFeedbackInMessages, showFeedback, closeFeedback } = useSuperChatFeedback({ messages, setMessages });

    const { messagesEndRef, scrollToBottom } = useSuperChatScroll({
        messages,
        showFeedbackInMessages
    });

    handleContextChangeRef.current = () => {
        setPreviousMessages(prev => [...prev, ...messages]);
        setMessages([]);

        if (clearFollowUpsRef.current) {
            clearFollowUpsRef.current();
        }
    };

    const handleContextChange = useCallback(() => {
        handleContextChangeRef.current?.();
    }, []);

    const {
        entryPointData, entryPointDataRef, contextualChipContextAware, updateEntryPointData, clearSelectedPrompt, newContextDetected
    } =
        useSuperChatContext({
            initialEntryPointData: InitialEntryPointData,
            page: props.page,
            contextPrompts: props.contextPrompts,
            messagesCount: messages.length,
            onContextChange: handleContextChange
        });

    const { followUps, hasRestoredFollowUps, restoreStoredFollowUps, clearFollowUps } = useSuperChatFollowUps({
        messages,
        previousMessages,
        sessionId,
        prompts,
        contextPrompts: props.contextPrompts,
        entryPointData
    });

    const {
        hiddenContextualChips, latestContextualChipIndex, setLatestContextualChipIndex, hideChip, resetHiddenChips, handleChipClick
    } =
        useSuperChatContextualChips({
            messages,
            entryPointData,
            sendMessageRef,
            socketServiceRef
        });

    const { sendExitAnalytics } = useSuperChatAnalytics({
        initialEntryPointData: InitialEntryPointData,
        isChatInitialized
    });

    const { socketService } = useSuperChatSocket({
        entryPointDataRef,
        setIsLoading,
        setIsChatInitialized,
        setHasSelectedPrompt,
        setMessages,
        setLatestContextualChipIndex,
        resetHiddenChips,
        setLoadingState: loading => {
            setIsLoading(loading);
            setRequestInFlight(loading);
        },
        onError: () => handleCloseRef.current?.(),
        onInitialize: () => initializeRef.current?.()
    });

    socketServiceRef.current = socketService;
    clearFollowUpsRef.current = clearFollowUps;

    const setLoadingState = loading => {
        setIsLoading(loading);
        setRequestInFlight(loading);
    };

    const initialize = async () => {
        try {
            let sessionFound = null;

            setIsLoadingPrevious(true);
            const conversationResponse = await fetchPreviousConversations(clientId, anonymousId);
            const mappedConversationsArr = mapPreviousConversations(conversationResponse);

            setPreviousMessages(mappedConversationsArr);
            setIsLoadingPrevious(false);
            setShowErrorMessage(false);

            if (mappedConversationsArr.length > 0) {
                sessionFound = findPreviousSession(conversationResponse, entryPointData);
            }

            // If session not found, remove sessionId from entryPointData.
            if (!sessionFound) {
                delete entryPointData.sessionId;
            }

            const sessionResponse = await createSession(sessionFound);
            const ecid = getEcid();
            socketService.connect(
                sessionResponse.token,
                sessionResponse.genai_pa_session_id,
                language,
                country,
                clientId,
                anonymousId,
                !!sessionFound,
                0,
                ecid
            );
            setSessionId(sessionResponse.genai_pa_session_id);
            setGENAISession({
                sessionId: sessionResponse.genai_pa_session_id,
                clientId: clientId,
                anonymousId: anonymousId,
                ...entryPointData
            });
            restoreStoredFollowUps(sessionResponse.genai_pa_session_id, entryPointData);
        } catch (error) {
            Sephora.logger.verbose('SuperChat: Failed to initialize Super Chat:', error);
            setLoadingState(false);
            setShowErrorMessage(true);
        }
    };

    const initializeWithNewContext = async newEntryPointData => {
        try {
            socketService.closeConnection();

            setIsChatInitialized(false);
            setHasSelectedPrompt(false);
            removeLastFollowUps();
            clearFollowUps();
            setIsLoading(true);

            const sessionResponse = await createSession();
            socketService.connect(
                sessionResponse.token,
                sessionResponse.genai_pa_session_id,
                language,
                country,
                clientId,
                anonymousId,
                false,
                0,
                getEcid()
            );

            setSessionId(sessionResponse.genai_pa_session_id);
            setGENAISession({
                sessionId: sessionResponse.genai_pa_session_id,
                clientId,
                anonymousId,
                ...newEntryPointData
            });
            restoreStoredFollowUps(sessionResponse.genai_pa_session_id, newEntryPointData);

            updateEntryPointData(newEntryPointData);
        } catch (error) {
            Sephora.logger.verbose('SuperChat: Failed to initialize Super Chat:', error);
            setLoadingState(false);
            setShowErrorMessage(true);
        }
    };

    initializeRef.current = initialize;

    // Re-fetch message history when user logs in mid-session
    useEffect(() => {
        const previousClientId = previousClientIdRef.current;
        previousClientIdRef.current = clientId;

        if (previousClientId !== clientId && clientId) {
            const refetchPreviousConversations = async () => {
                try {
                    setIsLoadingPrevious(true);
                    const conversationResponse = await fetchPreviousConversations(clientId, anonymousId);
                    const mappedConversationsArr = mapPreviousConversations(conversationResponse);
                    setPreviousMessages(mappedConversationsArr);
                    setMessages([]);
                    setIsLoadingPrevious(false);
                } catch (error) {
                    Sephora.logger.verbose('SuperChat: Failed to refetch previous conversations:', error);
                    setIsLoadingPrevious(false);
                }
            };

            refetchPreviousConversations();
        }
    }, [clientId]);

    useEffect(() => {
        const hasBackendResponse = messages.some(
            msg =>
                msg.response_type === RESPONSE_TYPES.CAMPAIGN_CONFIG_RESPONSE ||
                msg.response_type === RESPONSE_TYPES.FOLLOW_UP_MESSAGES_RESPONSE ||
                msg.response_type === MESSAGE_TYPES.USER_QUERY_RESPONSE
        );

        if (!hasSelectedPrompt || messages.length === 0 || !hasBackendResponse) {
            return;
        }

        const lastContextualChip = messages.findIndex(message => message.response_type === RESPONSE_TYPES.FOLLOW_UP_MESSAGES_RESPONSE);

        if (lastContextualChip !== -1) {
            hideChip(lastContextualChip);
        }

        setIsChatInitialized(true);

        const promptToSend = entryPointDataRef.current.selectedPrompt;
        clearSelectedPrompt();

        sendMessage(promptToSend, true);
        setHasSelectedPrompt(false);
    }, [messages, hasSelectedPrompt]);

    useEffect(() => {
        if (previousMessages.length > 0 && messages.length === 0 && hasSelectedPrompt && isChatInitialized) {
            const promptToSend = entryPointDataRef.current.selectedPrompt;
            clearSelectedPrompt();

            sendMessage(promptToSend, false);
            setHasSelectedPrompt(false);
        }

        if (previousMessages.length > 0) {
            scrollToBottom(true);
        }
    }, [previousMessages, hasSelectedPrompt, isChatInitialized]);

    useEffect(() => {
        const currentData = entryPointDataRef.current;

        if (!isLoading && currentData?.selectedPrompt && sessionId && isChatInitialized && !hasSelectedPrompt) {
            const entryPointId = JSON.stringify(currentData);

            if (lastProcessedEntryPointRef.current !== entryPointId) {
                lastProcessedEntryPointRef.current = entryPointId;

                const promptToSend = currentData.selectedPrompt;
                clearSelectedPrompt();
                sendMessage(promptToSend);
            }
        }
    }, [entryPointData]);

    const handleClose = () => {
        sendExitAnalytics();
        socketService.closeConnection();
        setLoadingState(false);
        closeSuperChat();
    };

    handleCloseRef.current = handleClose;

    const sendMessage = (message, addDelay = false) => {
        if (!message || !message.trim()) {
            return;
        }

        if (contextHasChanged(props.page)) {
            const { page } = props;
            const newEntryPointData = getNewEntryPoint(message, page);

            if (newEntryPointData) {
                initializeWithNewContext(newEntryPointData);

                return;
            }
        }

        // Store the last user message for resend functionality
        setLastUserMessage(message.trim());

        const sendQuery = () => {
            // Send the user query through the socket service
            const messageId = socketService.sendUserQuery(message.trim());

            if (messageId) {
                // Add the user message to the local messages state for immediate UI feedback
                const userMessage = {
                    type: EVENT_TYPES.MESSAGE,
                    message_type: MESSAGE_TYPES.USER_QUERY,
                    data: {
                        query: message.trim()
                    }
                };
                setMessages(prev => [...prev, userMessage]);

                // Show loading indicator
                setIsLoading(true);
                setShowErrorMessage(false);
            } else {
                // Handle case when messageId is not returned (send failed)
                setShowErrorMessage(true);
                setIsLoading(false);
            }
        };

        if (addDelay) {
            // Add 500ms delay for selectedPrompt to prevent timing issues with NEW_CONNECTION_REQUEST
            setTimeout(sendQuery, 500);
        } else {
            sendQuery();
        }
    };

    sendMessageRef.current = sendMessage;

    const renderInitialPrompts = () => {
        const initialDate = InitialEntryPointData?.date?.getTime();
        const currentDate = entryPointData?.date?.getTime();
        const hasNavigated = currentDate !== initialDate;
        const promptsToShow = hasNavigated ? props.contextPrompts : props.contextPrompts?.length > 0 ? props.contextPrompts : prompts;

        if (!promptsToShow?.length) {
            return null;
        }

        return (
            <PreSelectedPrompts
                prompts={promptsToShow}
                sendMessage={sendMessage}
                isDisabled={isLoading}
                pageContext={getSuperChatPageContextFromEntryPoint(entryPointData)}
            />
        );
    };

    // After page refresh from sign-in, restore lastMessageId and send USER_HAS_SIGNED_IN_EVENT
    useEffect(() => {
        const pending = getPendingSignInEvent();

        if (isChatInitialized && pending) {
            clearPendingSignInEvent();

            if (pending.lastMessageId) {
                socketService.lastMessageId = pending.lastMessageId;
            }

            setTimeout(() => {
                const messageId = socketService.sendSignInEvent();

                if (messageId) {
                    setIsLoading(true);
                }
            }, 500);
        }
    }, [isChatInitialized, socketService]);

    // Handle /signin link clicks — iOS pattern: keep socket alive, send USER_HAS_SIGNED_IN_EVENT
    const handleSignIn = useCallback(() => {
        trackSignInCTAClick({ pageContext: getSuperChatPageContextFromEntryPoint(entryPointData) });

        // Save lastMessageId so the page-refresh path can restore conversation chain
        setPendingSignInEvent(socketService.lastMessageId);

        showSignInModal({
            isOpen: true,
            callback: () => {
                const messageId = socketService.sendSignInEvent();

                if (messageId) {
                    // SPA path confirmed (socket alive) — safe to clear the flag now.
                    // Page-refresh path would have a dead socket and return null, leaving
                    // the flag intact for withSuperChatProps to pick up after reload.
                    setIsLoading(true);
                }
            }
        });
    }, [showSignInModal, socketService]);

    const handleResendMessage = () => {
        setShowErrorMessage(false);
        setLoadingState(false); // Allow resending when there's an error

        if (lastUserMessage) {
            // If user has provided a message before, resend it
            sendMessage(lastUserMessage);
        } else {
            // If no user message was provided (initial state), show loading and process as usual
            setLoadingState(true);
            // The socket service should handle the initial connection
            initialize();
        }
    };

    const displayComponents = useMemo(
        () =>
            createDisplayVisibility({
                previousMessages,
                messages,
                config,
                isLoadingPrevious,
                contextualChipContextAware,
                newContextDetected,
                entryPointData,
                followUps,
                isWelcomeMatEnabled: genAIChatBotWelcomeMat?.isEnabled && shouldShowWelcomeMat,
                welcomeMatCompletedOnMount: welcomeMatCompletedOnMount || welcomeMatPromptClicked
            }),
        [
            previousMessages,
            messages,
            config,
            isLoadingPrevious,
            contextualChipContextAware,
            newContextDetected,
            entryPointData,
            followUps,
            genAIChatBotWelcomeMat?.isEnabled,
            shouldShowWelcomeMat,
            welcomeMatPromptClicked
        ]
    );

    return (
        <>
            <div
                css={[
                    styles.fixed,
                    styles.backdrop,
                    {
                        [mediaQueries.smMax]: {
                            display: isSignInModalOpen ? 'none' : 'block'
                        }
                    }
                ]}
                style={{ opacity: 1 }}
            />
            <Flex
                ref={aiBeautyChatRef}
                css={[
                    styles.superChatContainer,
                    {
                        [mediaQueries.smMax]: {
                            zIndex: isSignInModalOpen ? zIndices.flyout : zIndices.superChat
                        }
                    }
                ]}
                right={[0, 3]}
            >
                <Flex
                    height='100%'
                    position='relative'
                    flexDirection='column'
                    css={styles.container(isGenAINewUIEnabled)}
                    overflow='hidden'
                >
                    {/* Header - positioned on top, full width */}
                    <Box css={styles.headerWrapper}>
                        <SuperChatHeader
                            title={config?.meta?.bot_open_modal_title || localization.title}
                            handleToggleFlyer={showFeedback}
                            showFlyer={false}
                            handleClose={handleClose}
                        />
                    </Box>

                    {/* Scrollable content area - scrollbar only here */}
                    <Flex
                        flex='1'
                        width='100%'
                        flexDirection='column'
                        css={styles.scrollContainer}
                    >
                        <Flex
                            padding={4}
                            flexDirection='column'
                            justifyContent='flex-start'
                            alignItems='flex-start'
                            gap={5}
                            width='100%'
                            paddingRight={4}
                        >
                            {displayComponents('welcomeMessageAndDisclaimer') && <InitialMessages config={config} />}
                            {displayComponents('previousMessages') && (
                                <Messages
                                    messages={previousMessages}
                                    sessionId={sessionId}
                                    sendMessage={sendMessage}
                                    hiddenContextualChips={hiddenContextualChips}
                                    onContextualChipClick={handleChipClick}
                                    latestContextualChipIndex={latestContextualChipIndex}
                                    isLoading={false}
                                    isPrevious={true}
                                    showFeedbackInMessages={showFeedbackInMessages}
                                    feedbackSteps={config?.feedback_steps}
                                    onCloseFeedback={closeFeedback}
                                    prompts={prompts}
                                    isRequestInFlight={isLoading}
                                    onSignIn={handleSignIn}
                                />
                            )}
                            {((contextualChipContextAware?.label && !hasRestoredFollowUps) || entryPointData?.category_id === CATEGORIES.GIFT) && (
                                <ContextualDivider
                                    label={contextualChipContextAware?.label}
                                    categoryId={entryPointData?.category_id}
                                />
                            )}
                            {displayComponents('contextualChipAndSelectedPrompts') && !hasRestoredFollowUps && (
                                <PreSelectedPrompts
                                    prompts={contextualChipContextAware?.followUps || []}
                                    sendMessage={sendMessage}
                                    isDisabled={isLoading}
                                    pageContext={getSuperChatPageContextFromEntryPoint(entryPointData)}
                                />
                            )}
                            {displayComponents('initialPrompts') && renderInitialPrompts()}
                            {displayComponents('initialMessages') && (
                                <InitialMessages
                                    config={config}
                                    hideWelcomeMessage={welcomeMatPromptClicked}
                                />
                            )}
                            {displayComponents('welcomeMat') && (
                                <WelcomeMat
                                    sendMessage={sendMessage}
                                    isDisabled={isLoading}
                                    config={config}
                                    onPromptClick={() => setWelcomeMatPromptClicked(true)}
                                />
                            )}
                            {displayComponents('messages') && (
                                <Messages
                                    messages={messages}
                                    sessionId={sessionId}
                                    sendMessage={sendMessage}
                                    hiddenContextualChips={hiddenContextualChips}
                                    onContextualChipClick={handleChipClick}
                                    latestContextualChipIndex={latestContextualChipIndex}
                                    isLoading={isLoading}
                                    showFeedbackInMessages={showFeedbackInMessages}
                                    feedbackSteps={config?.feedback_steps}
                                    onCloseFeedback={closeFeedback}
                                    prompts={prompts}
                                    isRequestInFlight={isLoading}
                                    onSignIn={handleSignIn}
                                />
                            )}
                            {displayComponents('contextualChip') && (
                                <ContextualChips
                                    key={followUps?.id}
                                    chips={followUps}
                                    sendMessage={sendMessage}
                                    onChipClick={chip => handleChipClick(chip)}
                                    isDisabled={isLoading}
                                    width={contextualChipWidth}
                                />
                            )}
                            <ErrorMessage
                                showErrorMessage={showErrorMessage}
                                text={localization.errorMessage}
                                resendYourMessage={localization.resendYourMessage}
                                resendMessage={handleResendMessage}
                            />
                            <div ref={messagesEndRef} />
                        </Flex>
                    </Flex>

                    {/* InputBar - positioned on bottom, full width */}
                    <Box css={styles.inputWrapper}>
                        <InputBar
                            sendMessage={sendMessage}
                            placeholder={localization.placeholder}
                            autoFocus
                            isDisabled={isLoading}
                        />
                    </Box>

                    {/* Full-page Share Feedback Overlay */}
                    <SuperChatFlyout
                        isOpen={showFlyer}
                        onShowFeedback={showFeedback}
                    />
                </Flex>
            </Flex>
        </>
    );
}

const styles = {
    superChatContainer: {
        position: 'fixed',
        right: 0,
        bottom: 0,
        width: '100%',
        maxWidth: 420,
        maxHeight: 720,
        height: '100%',
        zIndex: zIndices.flyout,
        [mediaQueries.smMax]: {
            bottom: 66,
            height: '80%'
        }
    },
    container: isGenAINewUIEnabled => ({
        boxShadow: '0px 0px 12px 0px rgba(0, 0, 0, 0.2)',
        borderTopLeft: 4,
        borderTopRight: 4,
        width: '100%',
        height: '100%',
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6,
        background: isGenAINewUIEnabled ? gradients.aiBeautyChat : 'white',
        [mediaQueries.smMax]: {
            boxShadow: 'none'
        }
    }),
    headerWrapper: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10
    },
    inputWrapper: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10
    },
    scrollContainer: {
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        paddingTop: '50px',
        paddingBottom: '72px'
    },
    backdrop: {
        backgroundColor: 'rgba(0,0,0,.25)',
        transition: 'opacity 300ms',
        display: 'none'
    },
    fixed: {
        position: 'fixed',
        inset: 0,
        zIndex: zIndices.flyout
    }
};

export default wrapFunctionalComponent(SuperChat, 'SuperChat');
